import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
  Dropdown,
  IDropdownOption,
  IconButton,
  Stack,
  StackItem,
  TextField,
  TooltipHost,
} from "@fluentui/react";
import { IFileInfo } from "ccs-azure";
import { AppTopContext } from "components/Welcome/Common/AppTopContext";
import DataProvider from "dataprovider/DataProvider";
import { IAppFileInfo, INavigationActions, IReturnAction, IUserAdditionalData, IUserBasicData, IUserFieldsState } from "Interfaces/IHRBrazil";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConstants } from "../../../../Utility/Constants";
import { ActionSection } from "./ActionSection";
import "./UserFormCommon.scss";
import "./BasicForm.scss";
import { Utility } from "components/Utility/Utility";
import { AppCalendar } from "components/Welcome/Common/AppCalendar";
import { BasicFormTXTEnum, SaveTypeEnum, ValidationType } from "components/Utility/AppEnums";
import { FileSizeMSG } from "components/Welcome/Common/FileSizeMSG";
import { AttachmentUploadIcon as AttachmentComponent } from "components/Welcome/Common/AttachmentUploadIcon";
import designerImage from "../../../../../images/Designer.jpg";
export interface IBasicFormProps {
  user: IUserFieldsState;
  isFormEditable: boolean;
  previousCall: any;
  nextCall: any;
  submitCall: any;
  userProfileFormData: IUserBasicData;
  userAdditionalFormData: IUserAdditionalData;
  navigationAction: INavigationActions;
  saveUserProfile: (sectionKey: string, data: any, updateData: boolean, callFunc: any, additionalData?: IUserAdditionalData) => void;
  updateFormMode: (formMode: boolean) => void;
}

export const BasicForm: React.FunctionComponent<IBasicFormProps> = ({ ...props }) => {
  const [_userProfileBasicData, _setUserProfileBasicData] = useState<IUserBasicData>(Utility.GetInitialData(AppConstants.SectionKey.BasicData));
  const [_userProfileAdditionalData, _setUserProfileAdditionalData] = useState<IUserAdditionalData>(
    Utility.GetInitialData(AppConstants.SectionKey.AdditionalData),
  );
  let [_isFileUpdated, _setIsFileUpdated] = useState<boolean>(false);
  let [_badgeDocs, _setBadgeDocs] = useState<Array<IFileInfo | File>>([]);
  let [_cv, _setCV] = useState<Array<IFileInfo | File>>([]);
  var dataProvider = new DataProvider();
  const appContext = useContext(AppTopContext);
  const { t, i18n } = useTranslation(AppConstants.i18NameSpaces);
  let [_genderOptions, _setGenderOptions] = useState<IDropdownOption[]>([]);
  let [_nationalityOptions, _setNationalityOptions] = useState<IDropdownOption[]>([]);
  let [_raceColorOptions, _setRaceColorOptions] = useState<IDropdownOption[]>([]);
  let [_birthCountryOptions, _setBirthCountryOptions] = useState<IDropdownOption[]>([]);
  let [_deletedDocs, _setDeletedDocs] = useState<Array<IFileInfo>>([]);
  let [_fieldErrors, _setFieldErrors] = useState<string[]>([]);
  let [_showErrors, _setShowErrors] = useState<boolean>(false);
  let [_showBadgePhotoDialog, _setShowBadgePhotoDialog] = useState<boolean>(false);

  useEffect(() => {
    initializeForm();
  }, [props.userProfileFormData, props.userAdditionalFormData]);

  /// update dll according to localize
  useEffect(() => {
    var options = Utility.GetChoiceOptions(i18n.language, props, AppConstants.choices.Gender);
    _setGenderOptions(options);
    _setNationalityOptions(Utility.GetChoiceOptions(i18n.language, props, AppConstants.choices.Nationality));
    _setRaceColorOptions(Utility.GetChoiceOptions(i18n.language, props, AppConstants.choices.RaceColor));
    _setBirthCountryOptions(Utility.GetChoiceOptions(i18n.language, props, AppConstants.choices.Country));
  }, [appContext.IsLBrazil, (props as any).currentUser?.AppChoices]);

  useEffect(() => {
    if (props.navigationAction.Guid) {
      if (props.isFormEditable) {
        // Block left-nav navigation when required fields are empty.
        const navErrors = getBasicFieldErrors();
        if (_badgeDocs.length === 0) {
          navErrors.push(t("UserBasicData.FormFields.BadgePhoto"));
        }
        if (_cv.length === 0) {
          navErrors.push(t("UserBasicData.FormFields.CV"));
        }
        if (navErrors.length > 0) {
          _setFieldErrors(navErrors);
          _setShowErrors(true);
          return;
        }
        let isFormUpdated = isBasicSectionUpdated();
        if (appContext.IsUserAdmin) {
          if (isFormUpdated) {
            appContext.ShowAppModal(t("Information.AdminPanelAction.Header"), t("Information.AdminPanelAction.MSG"));
          } else {
            props.navigationAction.NavigationFun();
          }
        } else {
          callPropSaveDataCallBackFunction(isFormUpdated, props.navigationAction.NavigationFun);
        }
      } else {
        props.navigationAction.NavigationFun();
      }
    }
  }, [props.navigationAction.Guid]);

  function initializeForm() {
    let initialData = Utility.GetInitialData(AppConstants.SectionKey.BasicData, props.userProfileFormData) as IUserBasicData;
    let initialAdditionalData = Utility.GetInitialData(AppConstants.SectionKey.AdditionalData, props.userAdditionalFormData) as IUserAdditionalData;
    _setBadgeDocs(Utility.GetFiles(initialData.BadgePhoto));
    _setCV(Utility.GetFiles(initialData.CV));
    _setUserProfileBasicData(initialData);
    _setUserProfileAdditionalData(initialAdditionalData);
    _setDeletedDocs([]);
  }
  function resetForm() {
    /// check for change and reset
    let isFormDataUpdated = isBasicSectionUpdated();
    if (isFormDataUpdated) {
      appContext.ShowAppConfirmationModal(
        t("ActionBtn.ExitEditModeConfirmation.Header"),
        t("ActionBtn.ExitEditModeConfirmation.MSG"),
        t("ActionBtn.ExitEditModeConfirmation.OKBtnTxt"),
        t("ActionBtn.ExitEditModeConfirmation.CancelBtnTxt"),
        function () {
          /// call reset data
          initializeForm();
        },
      );
    } else {
      props.updateFormMode(false);
    }
  }

  function callPropSaveDataCallBackFunction(isDataUpdateRequire: boolean, func: any) {
    appContext.ShowAppLoader(true, "");
    if (isDataUpdateRequire) {
      if (appContext.IsUserAdmin) {
        /// validate page data
        if (!Utility.IsBasicFormValidated(_userProfileBasicData, ValidationType.FieldOnly, _userProfileAdditionalData)) {
          appContext.ShowAppModal(t("ModalDialogHeader.ValidationMessage"), t("ValidationMessage.RequiredFieldValidation"));
          appContext.ShowAppLoader(false, "");
          return;
        }
        if (!Utility.IsRequiredFormDocsPresent([_badgeDocs, _cv])) {
          appContext.ShowAppModal(t("ModalDialogHeader.ValidationMessage"), t("ValidationMessage.RequiredFieldValidation"));
          appContext.ShowAppLoader(false, "");
          return;
        }
      }
      if (!Utility.IsFilesSizeValid([_badgeDocs, _cv])) {
        appContext.ShowAppModal(t("ModalDialogHeader.ValidationMessage"), t("ValidationMessage.FileSizeLimit"));
        appContext.ShowAppLoader(false, "");
        return;
      }
      dataProvider
        .deleteUserDocs(props.user.UserId, _deletedDocs)
        .then((delResponse: any) => {
          if (delResponse?.hasError) {
            console.log(delResponse);
            if (!Utility.IsUnauthorizeError(delResponse.errorDetail, appContext)) {
              appContext.ShowAppModal(t("ModalDialogHeader.Error"), t("Error.DocumentError.DocumentDeleteError"));
            }
            appContext.ShowAppLoader(false, "");
          } else {
            Promise.all([
              dataProvider.uploadUserDocs(props.user.UserId, AppConstants.AttachmentType.BadgePhotoDocs.Display, _badgeDocs),
              dataProvider.uploadUserDocs(props.user.UserId, AppConstants.AttachmentType.CV.Display, _cv),
            ])
              .then((res) => {
                let errorMSG = false;
                if (!res[0]?.hasError) {
                  _userProfileBasicData.BadgePhoto = res[0].data as IAppFileInfo[];
                  if (_userProfileBasicData.BadgePhoto.length > 0) {
                    /// only single file require in json
                    _userProfileBasicData.BadgePhoto = [_userProfileBasicData.BadgePhoto[0]];
                  }
                } else {
                  console.log(res[0]?.ErrorDetail);
                  errorMSG = true;
                }
                if (!res[1]?.hasError) {
                  _userProfileBasicData.CV = res[1].data as IAppFileInfo[];
                  if (_userProfileBasicData.CV.length > 0) {
                    /// only single file require in json
                    _userProfileBasicData.CV = [_userProfileBasicData.CV[0]];
                  }
                } else {
                  console.log(res[1]?.ErrorDetail);
                  errorMSG = true;
                }
                if (errorMSG) {
                  appContext.ShowAppModal(t("ModalDialogHeader.Error"), t("Error.DocumentError.DocumentUploadError"));
                  appContext.ShowAppLoader(false, "");
                } else {
                  props.saveUserProfile(AppConstants.SectionKey.BasicData, _userProfileBasicData, isDataUpdateRequire, func, _userProfileAdditionalData);
                }
              })
              .catch((err) => {
                appContext.ShowAppModal(t("ModalDialogHeader.Error"), t("Error.DocumentError.GenericError"));
                console.log(err);
                appContext.ShowAppLoader(false, "");
              });
          }
        })
        .catch((err) => {
          appContext.ShowAppModal(t("ModalDialogHeader.Error"), t("Error.DocumentError.GenericError"));
          console.log(err);
          appContext.ShowAppLoader(false, "");
        });
    } else {
      func();
    }
  }
  const onFileDeleted =
    (attachmentType: string) =>
    (file: IFileInfo | File): Promise<void> => {
      let attachments = [];
      let deletedAttachments = [];
      let idx = -1;
      switch (attachmentType) {
        case AppConstants.AttachmentType.BadgePhotoDocs.Type:
          attachments = [..._badgeDocs];
          idx = attachments.indexOf(file);
          if (idx !== -1) {
            attachments.splice(idx, 1);
            _setBadgeDocs(attachments);
          }
          break;
        case AppConstants.AttachmentType.CV.Type:
          attachments = [..._cv];
          idx = attachments.indexOf(file);
          if (idx !== -1) {
            attachments.splice(idx, 1);
            _setCV(attachments);
          }
          break;
        default:
          break;
      }
      if (file instanceof File) {
      } else {
        deletedAttachments = [..._deletedDocs];
        deletedAttachments.push(file);
        _setDeletedDocs(deletedAttachments);
      }
      _setIsFileUpdated(true);
      return Promise.resolve(null);
    };
  const onFileDownload = (file: IFileInfo): Promise<void> => {
    appContext.ShowAppLoader(true, "");
    dataProvider
      .downloadUserDoc(props.user.UserId, file.fileId, file.fileName)
      .then((res) => {
        let r = res;
        appContext.ShowAppLoader(false, "");
      })
      .catch((err) => {
        console.log(err);
        appContext.ShowAppLoader(false, "");
        appContext.ShowAppModal(t("ModalDialogHeader.Error"), t("Error.DocumentError.FileDownloadError"));
      });
    return Promise.resolve(null);
  };
  const onFilesAdded =
    (attachmentType: string) =>
    (files: File[]): Promise<void> => {
      let attachments = [];
      switch (attachmentType) {
        case AppConstants.AttachmentType.BadgePhotoDocs.Type:
          attachments = [..._badgeDocs];
          files.forEach((file) => {
            attachments.push(file);
          });
          _setBadgeDocs(attachments);
          break;
        case AppConstants.AttachmentType.CV.Type:
          attachments = [..._cv];
          files.forEach((file) => {
            attachments.push(file);
          });
          _setCV(attachments);
          break;
        default:
          break;
      }
      _setIsFileUpdated(true);
      return Promise.resolve();
    };
  function _onChangeText(ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string, fieldType: BasicFormTXTEnum) {
    let userOptions = JSON.parse(JSON.stringify(_userProfileBasicData)) as IUserBasicData;
    switch (fieldType) {
      case BasicFormTXTEnum.FatherName:
        userOptions.FatherName = Utility.toECFormat(text);
        break;
      case BasicFormTXTEnum.FullName:
        userOptions.FullName = Utility.toECFormat(text);
        break;
      case BasicFormTXTEnum.MotherName:
        userOptions.MotherName = Utility.toECFormat(text);
        break;
      default:
        break;
    }
    _setUserProfileBasicData(userOptions);
  }
  function _onGenderChange(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
    _setUserProfileBasicData({
      ..._userProfileBasicData,
      Gender: item.key as string,
    });
  }

  function _onAdditionalDataChange(field: "Nationality" | "Race", item: IDropdownOption) {
    _setUserProfileAdditionalData({
      ..._userProfileAdditionalData,
      [field]: item.key as string,
    });
  }

  function _onBirthCountryChange(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
    _setUserProfileAdditionalData({
      ..._userProfileAdditionalData,
      Birthplace: {
        ..._userProfileAdditionalData.Birthplace,
        Country: item.key as string,
      },
    });
  }

  function _onBirthplaceTextChange(field: "State" | "City", text: string) {
    _setUserProfileAdditionalData({
      ..._userProfileAdditionalData,
      Birthplace: {
        ..._userProfileAdditionalData.Birthplace,
        [field]: Utility.toECFormat(text),
      },
    });
  }

  function isBasicSectionUpdated(): boolean {
    return (
      Utility.IsFormUpdated(_isFileUpdated, AppConstants.SectionKey.BasicData, props.user, _userProfileBasicData) ||
      Utility.IsFormUpdated(false, AppConstants.SectionKey.AdditionalData, props.user, _userProfileAdditionalData)
    );
  }

  function getBasicFieldErrors(): string[] {
    const errors = Utility.GetFieldErrors(AppConstants.SectionKey.BasicData, _userProfileBasicData);
    if (!_userProfileAdditionalData?.Nationality?.trim()) {
      errors.push(t("UserAdditionalData.FormFields.Nationality"));
    }
    if (!_userProfileAdditionalData?.Race?.trim()) {
      errors.push(t("UserAdditionalData.FormFields.RaceColor"));
    }
    if (!_userProfileAdditionalData?.Birthplace?.Country?.trim()) {
      errors.push(t("UserAdditionalData.FormFields.BirthplaceSection.Country"));
    }
    if (!_userProfileAdditionalData?.Birthplace?.State?.trim()) {
      errors.push(t("UserAdditionalData.FormFields.BirthplaceSection.State"));
    }
    if (!_userProfileAdditionalData?.Birthplace?.City?.trim()) {
      errors.push(t("UserAdditionalData.FormFields.BirthplaceSection.City"));
    }
    return errors;
  }

  function actionCallbackFun(saveType: SaveTypeEnum) {
    // Validate required fields on Next for all users and block navigation when invalid.
    if (props.isFormEditable && (saveType === SaveTypeEnum.NextSave || saveType === SaveTypeEnum.Next)) {
      const errors = getBasicFieldErrors();
      if (_badgeDocs.length === 0) {
        errors.push(t("UserBasicData.FormFields.BadgePhoto"));
      }
      if (_cv.length === 0) {
        errors.push(t("UserBasicData.FormFields.CV"));
      }
      if (errors.length > 0) {
        _setFieldErrors(errors);
        _setShowErrors(true);
        return;
      }
    }
    _setFieldErrors([]);
    _setShowErrors(false);
    let actionProps: IReturnAction = Utility.GetActionProps(saveType, props.previousCall, props.nextCall);
    if (actionProps.saveRequired) {
      actionProps.saveRequired = isBasicSectionUpdated();
    }
    callPropSaveDataCallBackFunction(actionProps.saveRequired, actionProps.callBack);
  }

  // Returns a localized error message for a field when it is empty and errors are being shown.
  function getFieldError(isEmpty: boolean, labelKey: string): string {
    if (!_showErrors || !isEmpty) return "";
    return t("ValidationMessage.RequiredField").replace("{field}", t(labelKey));
  }

  return (
    <Stack>
      <Stack className="formHeader">{t("UserBasicData.FormHeader")}</Stack>
      <ActionSection
        resetForm={resetForm}
        isFormEditable={props.isFormEditable}
        {...props}
        previousCallFun={undefined}
        submitCallFun={undefined}
        nextCallFun={actionCallbackFun}
      />
      {_showErrors && _fieldErrors.length > 0 && (
        <div className="fieldErrorSummary" role="alert">
          <strong>{t("ModalDialogHeader.ValidationMessage")}</strong>
          <ul>
            {_fieldErrors.map((fieldLabel, idx) => (
              <li key={idx}>{t("ValidationMessage.RequiredField").replace("{field}", fieldLabel)}</li>
            ))}
          </ul>
        </div>
      )}
      <Stack className="formSection formDetails" horizontal>
        <StackItem className="formSubSection">
          <Stack>
            <StackItem className="fields">
              <Stack className="field">
                <StackItem className="label">
                  {t("UserBasicData.FormFields.FullName")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value">
                  <TextField
                    disabled={!props.isFormEditable}
                    autoComplete="off"
                    value={Utility.toECFormat(_userProfileBasicData.FullName)}
                    errorMessage={getFieldError(!_userProfileBasicData.FullName || !_userProfileBasicData.FullName.trim(), "UserBasicData.FormFields.FullName")}
                    onChange={(ev, text) => {
                      _onChangeText(ev, text, BasicFormTXTEnum.FullName);
                    }}
                  ></TextField>
                </StackItem>
              </Stack>
              <Stack className="field">
                <StackItem className="label">
                  {t("UserBasicData.FormFields.Gender")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value">
                  <Dropdown
                    disabled={!props.isFormEditable}
                    calloutProps={{ directionalHintFixed: true }}
                    options={_genderOptions}
                    onChange={_onGenderChange}
                    selectedKey={_userProfileBasicData.Gender}
                    errorMessage={getFieldError(!_userProfileBasicData.Gender || !_userProfileBasicData.Gender.trim(), "UserBasicData.FormFields.Gender")}
                  />
                </StackItem>
              </Stack>
              <Stack className="field">
                <StackItem className="label">
                  {t("UserBasicData.FormFields.BirthDate")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value">
                  <AppCalendar
                    IsEditable={props.isFormEditable}
                    InitialDate={_userProfileBasicData.BirthDate ? new Date(_userProfileBasicData.BirthDate) : null}
                    GetCalendatDate={function (date: Date): void {
                      _userProfileBasicData.BirthDate = date.toUTCString();
                      _setUserProfileBasicData(_userProfileBasicData);
                    }}
                  />
                  {getFieldError(!_userProfileBasicData.BirthDate, "UserBasicData.FormFields.BirthDate") && (
                    <div style={{ color: "#a4262c", fontSize: 12, paddingTop: 5 }}>
                      {getFieldError(!_userProfileBasicData.BirthDate, "UserBasicData.FormFields.BirthDate")}
                    </div>
                  )}
                </StackItem>
              </Stack>

              <Stack className="field">
                <StackItem className="label">
                  {t("UserBasicData.FormFields.MotherName")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value">
                  <TextField
                    disabled={!props.isFormEditable}
                    autoComplete="off"
                    value={Utility.toECFormat(_userProfileBasicData.MotherName)}
                    errorMessage={getFieldError(
                      !_userProfileBasicData.MotherName || !_userProfileBasicData.MotherName.trim(),
                      "UserBasicData.FormFields.MotherName",
                    )}
                    onChange={(ev, text) => {
                      _onChangeText(ev, text, BasicFormTXTEnum.MotherName);
                    }}
                  ></TextField>
                </StackItem>
              </Stack>
              <Stack className="field">
                <StackItem className="label">{t("UserBasicData.FormFields.FatherName")}</StackItem>
                <StackItem className="value">
                  <TextField
                    disabled={!props.isFormEditable}
                    autoComplete="off"
                    value={Utility.toECFormat(_userProfileBasicData.FatherName)}
                    onChange={(ev, text) => {
                      _onChangeText(ev, text, BasicFormTXTEnum.FatherName);
                    }}
                  ></TextField>
                </StackItem>
              </Stack>
              <Stack className="field">
                <StackItem className="label">
                  {t("UserAdditionalData.FormFields.Nationality")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value">
                  <Dropdown
                    disabled={!props.isFormEditable}
                    calloutProps={{ directionalHintFixed: true }}
                    options={_nationalityOptions}
                    selectedKey={_userProfileAdditionalData?.Nationality}
                    errorMessage={getFieldError(!_userProfileAdditionalData?.Nationality?.trim(), "UserAdditionalData.FormFields.Nationality")}
                    onChange={(event, item) => _onAdditionalDataChange("Nationality", item)}
                  />
                </StackItem>
              </Stack>
              <Stack className="field">
                <StackItem className="label">
                  {t("UserAdditionalData.FormFields.RaceColor")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value">
                  <Dropdown
                    disabled={!props.isFormEditable}
                    calloutProps={{ directionalHintFixed: true }}
                    options={_raceColorOptions}
                    selectedKey={_userProfileAdditionalData?.Race}
                    errorMessage={getFieldError(!_userProfileAdditionalData?.Race?.trim(), "UserAdditionalData.FormFields.RaceColor")}
                    onChange={(event, item) => _onAdditionalDataChange("Race", item)}
                  />
                </StackItem>
              </Stack>
              <fieldset className="fieldsetsection">
                <legend className="legentStyle">{t("UserAdditionalData.FormFields.BirthplaceSectionName")}</legend>
                <Stack>
                  <Stack className="field">
                    <StackItem className="label">
                      {t("UserAdditionalData.FormFields.BirthplaceSection.Country")}
                      <span className="imp">*</span>
                    </StackItem>
                    <StackItem className="value">
                      <Dropdown
                        disabled={!props.isFormEditable}
                        calloutProps={{ directionalHintFixed: true }}
                        options={_birthCountryOptions}
                        selectedKey={_userProfileAdditionalData?.Birthplace?.Country}
                        errorMessage={getFieldError(
                          !_userProfileAdditionalData?.Birthplace?.Country?.trim(),
                          "UserAdditionalData.FormFields.BirthplaceSection.Country",
                        )}
                        onChange={_onBirthCountryChange}
                      />
                    </StackItem>
                  </Stack>
                  <Stack className="field">
                    <StackItem className="label">
                      {t("UserAdditionalData.FormFields.BirthplaceSection.State")}
                      <span className="imp">*</span>
                    </StackItem>
                    <StackItem className="value">
                      <TextField
                        disabled={!props.isFormEditable}
                        autoComplete="off"
                        value={Utility.toECFormat(_userProfileAdditionalData?.Birthplace?.State)}
                        errorMessage={getFieldError(
                          !_userProfileAdditionalData?.Birthplace?.State?.trim(),
                          "UserAdditionalData.FormFields.BirthplaceSection.State",
                        )}
                        onChange={(event, text) => _onBirthplaceTextChange("State", text)}
                      />
                    </StackItem>
                  </Stack>
                  <Stack className="field">
                    <StackItem className="label">
                      {t("UserAdditionalData.FormFields.BirthplaceSection.City")}
                      <span className="imp">*</span>
                    </StackItem>
                    <StackItem className="value">
                      <TextField
                        disabled={!props.isFormEditable}
                        autoComplete="off"
                        value={Utility.toECFormat(_userProfileAdditionalData?.Birthplace?.City)}
                        errorMessage={getFieldError(
                          !_userProfileAdditionalData?.Birthplace?.City?.trim(),
                          "UserAdditionalData.FormFields.BirthplaceSection.City",
                        )}
                        onChange={(event, text) => _onBirthplaceTextChange("City", text)}
                      />
                    </StackItem>
                  </Stack>
                </Stack>
              </fieldset>
            </StackItem>
          </Stack>
        </StackItem>
        <StackItem className="formSubSection">
          <Stack>
            <StackItem className="fields">
              <Stack className="field">
                <StackItem className="label">
                  {t("UserBasicData.FormFields.BadgePhoto")}
                  <span className="imp">*</span>
                  <TooltipHost
                    content={
                      <div
                        className="badgePhotoTooltipDialog"
                        onClick={() => _setShowBadgePhotoDialog(true)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="badgePhotoTooltipHeader">{t("UserBasicData.FormFields.BadgePhotoModalTitle")}</div>
                        <img className="badgePhotoTooltipImg" src={designerImage} alt="Valid badge example" />
                      </div>
                    }
                    directionalHint={DirectionalHint.rightCenter}
                    closeDelay={300}
                    calloutProps={{
                      gapSpace: 6,
                      className: "badgePhotoTooltipCallout",
                    }}
                  >
                    <IconButton
                      iconProps={{ iconName: "Info" }}
                      className="badgePhotoInfoIcon"
                      ariaLabel={t("UserBasicData.FormFields.BadgePhotoTooltip")}
                      title={t("UserBasicData.FormFields.BadgePhotoTooltip")}
                      onClick={() => _setShowBadgePhotoDialog(true)}
                    />
                  </TooltipHost>
                </StackItem>
                <StackItem className="value" style={{ overflowWrap: "anywhere" }}>
                  <AttachmentComponent
                    texts={Utility.getAttachmentTexts()}
                    fileMode="SingleFile"
                    attachment={_badgeDocs.length > 0 ? _badgeDocs[0] : null}
                    enableDropZone={true}
                    extensionFilters={AppConstants.fileExtensionAllowed}
                    onFileDeleted={onFileDeleted(AppConstants.AttachmentType.BadgePhotoDocs.Type)}
                    onFileDownload={onFileDownload}
                    onFilesAdded={onFilesAdded(AppConstants.AttachmentType.BadgePhotoDocs.Type)}
                    readonly={!props.isFormEditable}
                  ></AttachmentComponent>
                  {_showErrors && _badgeDocs.length === 0 && (
                    <div className="fieldErrorText">{t("ValidationMessage.RequiredField").replace("{field}", t("UserBasicData.FormFields.BadgePhoto"))}</div>
                  )}
                  <FileSizeMSG files={_badgeDocs} />
                </StackItem>
              </Stack>
              <Stack className="field">
                <StackItem className="label">
                  {t("UserBasicData.FormFields.CV")}
                  <span className="imp">*</span>
                </StackItem>
                <StackItem className="value" style={{ overflowWrap: "anywhere" }}>
                  <AttachmentComponent
                    texts={Utility.getAttachmentTexts()}
                    fileMode="SingleFile"
                    attachment={_cv.length > 0 ? _cv[0] : null}
                    enableDropZone={true}
                    extensionFilters={AppConstants.fileExtensionAllowed}
                    onFileDeleted={onFileDeleted(AppConstants.AttachmentType.CV.Type)}
                    onFileDownload={onFileDownload}
                    onFilesAdded={onFilesAdded(AppConstants.AttachmentType.CV.Type)}
                    readonly={!props.isFormEditable}
                  ></AttachmentComponent>
                  {_showErrors && _cv.length === 0 && (
                    <div className="fieldErrorText">{t("ValidationMessage.RequiredField").replace("{field}", t("UserBasicData.FormFields.CV"))}</div>
                  )}
                  <FileSizeMSG files={_cv} />
                </StackItem>
              </Stack>
            </StackItem>
          </Stack>
        </StackItem>
      </Stack>
      {_showBadgePhotoDialog && (
        <Dialog
          hidden={!_showBadgePhotoDialog}
          onDismiss={() => _setShowBadgePhotoDialog(false)}
          dialogContentProps={{
            type: DialogType.normal,
            title: t("UserBasicData.FormFields.BadgePhotoModalTitle"),
          }}
          modalProps={{
            isBlocking: false,
            className: "badgePhotoDialogModal",
            styles: {
              main: {
                maxWidth: "1200px !important",
                minWidth: "min(1100px, 90vw)",
                width: "90%",
                maxHeight: "90vh",
              },
            },
          }}
        >
          <div className="badgePhotoDialogContent">
            <img className="badgePhotoDialogImage" src={designerImage} alt="Valid badge example" />
          </div>
          <DialogFooter>
            <DefaultButton onClick={() => _setShowBadgePhotoDialog(false)} text={t("AttachmentTexts.labelButtonOk") || "Ok"} />
          </DialogFooter>
        </Dialog>
      )}
    </Stack>
  );
};
